import { parseTaskKey } from '@pm-tool/shared';
import type { QueryClient } from '@tanstack/react-query';
import { createFileRoute, notFound, redirect } from '@tanstack/react-router';
import { requireProjectsFeature } from '@/features/projects/guard';
import { ProjectLayout } from '@/features/projects/project-layout';
import { projectByKeyQuery } from '@/features/projects/projects-api';
import { taskByKeyQuery } from '@/features/tasks/tasks-api';
import { ApiError } from '@/lib/api-client';

/** Project pages under /p/{KEY}. Unknown and invisible projects look the same (404). */
export const Route = createFileRoute('/_app/p/$projectKey')({
  beforeLoad: ({ context }) => requireProjectsFeature(context.queryClient),
  loader: async ({ context, params, location }) => {
    try {
      await context.queryClient.query(projectByKeyQuery(params.projectKey));
    } catch (error) {
      if (error instanceof ApiError && (error.status === 404 || error.status === 400)) {
        await redirectMovedTask(context.queryClient, location.pathname);
        throw notFound();
      }
      throw error;
    }
  },
  component: RouteComponent,
});

/**
 * TASK-07: the old project of a moved task may be invisible (or the key may belong to another
 * project). If /p/{KEY}/{TASK} names a task the user can see, continue at its current URL.
 */
async function redirectMovedTask(queryClient: QueryClient, pathname: string) {
  const segment = pathname.split('/')[3];
  if (segment === undefined || parseTaskKey(segment) === null) return;
  const task = await queryClient.query(taskByKeyQuery(segment)).catch(() => undefined);
  if (task === undefined) return;
  throw redirect({
    to: '/p/$projectKey/$taskKey',
    params: { projectKey: task.project.key, taskKey: task.key },
    replace: true,
  });
}

function RouteComponent() {
  const { projectKey } = Route.useParams();
  return <ProjectLayout projectKey={projectKey} />;
}
